import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
  OnInit,
  Signal,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { SearchPanelComponent } from './search-panel/search-panel.component';
import { FooterComponent } from '../../../layout/footer/footer.component';
import { HeaderComponent } from '../../../layout/header/header.component';
import { ResponseViewComponent } from './response-view/response-view.component';
import { SearchType } from '../../enums/search-type';
import { AnswerSearch } from '../../models/recent-search';
import { OpenSearchService } from '../../services/open-search.service';
import { TextareaSearchComponent } from './textarea-search/textarea-search.component';
import { ChangeLanguagePipe } from '../../pipes/change-language.pipe';
import { TranslatableField } from '../../models/TranslatableField';
import { UserService } from '../../services/user.service';
import { OnBoardingService } from '../../services/on-boarding.service';
import { StepHomeOpenSearch } from '../../constants/steps-boarding.contasts';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { DrawerSearchPanelService } from '../../../../core/shared/services/drawer-search-panel.service';
import { LessonsProjectService } from '../../services/lessons-project.service';
import { OpenSearchDrawerService } from '../../services/open-search-drawer.service';
import { SearchKeywordComponent } from './response-view/search-keyword/search-keyword.component';
import { ScrollToTextPayload } from '../../models/ScrollToTextPayload';

@Component({
  selector: 'app-open-search',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HeaderComponent,
    FooterComponent,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    FormsModule,
    SearchPanelComponent,
    ResponseViewComponent,
    TextareaSearchComponent,
    MatMenuModule,
    MatButtonModule,
    SearchKeywordComponent,
  ],
  templateUrl: './open-search.component.html',
  styleUrl: './open-search.component.scss',
})
export class OpenSearchComponent implements OnInit {
  private projectsService = inject(LessonsProjectService);
  changeLanguage = inject(ChangeLanguagePipe);
  drawerService = inject(OpenSearchDrawerService);
  searchType = model<SearchType>();
  isSelecteditem = model<boolean>();
  isShowAllResults = model<boolean>(false);
  showSearchPanel = model<boolean>();
  items = model<AnswerSearch[]>([]);
  inputText = model<TranslatableField | null>(null);
  queryMode = model<'default' | 'curated-lessons'>('default');
  showAllResults = model<boolean>(false);
  showResponseView = model<boolean>(false);
  showRecentSearchPanel = signal(false);
  showQuickSearchPanel = signal(false);
  quick = signal(SearchType.QUICK);
  recent = signal(SearchType.RECENT);
  isQuickActive = computed(() => this.searchType() === this.quick());
  isRecentActive = computed(() => this.searchType() === this.recent());
  activeDestroyComponent = model<boolean>(false);
  userService = inject(UserService);
  user = computed(() => this.userService.user());
  userPhoto = computed(() => this.user()?.photo || '/images/unknown-user.png');
  firstName = computed(
    () => this.user()?.firstName?.toString() || this.user()?.fullName?.toString() || '',
  );
  private onBoardingService = inject(OnBoardingService);
  private onBoardinbgKey = 'OpenSearch-onboarding';
  private drawerSearchPanel = inject(DrawerSearchPanelService);
  readonly showDisclaimer = signal<boolean>(true);
  countRequest = 0;
  projectsUnderPreparation: Signal<any[]> = computed(() =>
    this.projectsService.projectsUnderPreparation(),
  );
  projectsExecution: Signal<any[]> = computed(() => this.projectsService.projectsExecution());
  isLoading = this.projectsService.isLoadingProjects;
  noProjectsFound = computed(
    () => this.projectsUnderPreparation()?.length === 0 && this.projectsExecution()?.length === 0,
  );
  isDrawerOpen = computed(() => this.drawerSearchPanel.state().open);
  spinnerConversational = computed(() => this.openSearchService.spinnerConversational());
  historyResponse = computed(() => this.openSearchService.historyResponse());
  @ViewChild(ResponseViewComponent) responseView!: ResponseViewComponent;
  @ViewChild(SearchPanelComponent) searchpanel!: SearchPanelComponent;

  constructor(private openSearchService: OpenSearchService) {
    this.userService.fetchUser();
    this.effectSearchType();
    this.effectIrtemSelected();
    this.effectListenDestroy();
    this.effectShowSearchPanel();
    this.effectIsShowAllResults();
    this.effectKeepViewStateInSync();
    this.effectResetResponseViewWhenConversationIsCleared();
    this.effectSyncSelectionFromDrawer();
    this.effectShowResponseViewOnConversationLoad();
    const dataHistory = this.openSearchService._dataOpenLogic();
    if (dataHistory) {
      const parsed = dataHistory;
      this.showAllResults.set(parsed.showAllResults);
      this.activeDestroyComponent.set(parsed.activeDestroyComponent);
      this.showResponseView.set(parsed.showResponseView);
      const newOpenSearch = localStorage.getItem('OpenSearchPath');
      if (newOpenSearch) {
        setTimeout(() => {
          if (this.responseView) {
            this.responseView.showOnlyNewSearch.set(true);
          }
          this.drawerSearchPanel.close();
          this.drawerService.close();
          this.openSearchService.historyResponse.set(null);
          this.openSearchService.conversationIdSignal.set(null);
          this.showSearchPanel.set(false);
          this.showAllResults.set(false);
        }, 200);
      }
    } else {
      localStorage.removeItem('lastOpenSearchPath');
      localStorage.removeItem('responseView');
    }
    effect(() => {
      if (this.showAllResults() && this.searchType() === this.recent()) {
        localStorage.setItem('lastOpenSearchPath', 'recent');
      }
      if (this.showAllResults() && this.searchType() === this.quick()) {
        localStorage.setItem('lastOpenSearchPath', 'quick');
      }
    });
    effect(() => {
      if (!this.showAllResults()) {
        this.showQuickSearchPanel.set(false);
        this.showRecentSearchPanel.set(false);
      }
    });
    setTimeout(() => {
      if (!this.showResponseView() && !this.showAllResults()) {
        this.openQuickSearch();
      }
    });
    effect(() => {
      const isExploreall = localStorage.getItem('lastOpenSearchPath');
      if (!isExploreall) {
        if (this.countRequest === 0 && !this.isLoading()) {
          setTimeout(() => {
            this.startOnboarding();
          }, 500);
          this.countRequest += 1;
        }
      }
    });
  }
  onScrollToText(payload: ScrollToTextPayload): void {
    this.responseView?.scrollToExactText(payload);
  }
  effectShowResponseViewOnConversationLoad() {
    effect(() => {
      const history = this.openSearchService.historyResponse();
      if (
        history?.openQueryInteractions &&
        history.openQueryInteractions.length > 0 &&
        !this.showResponseView()
      ) {
        this.showResponseView.set(true);
        this.showAllResults.set(false);
        this.showRecentSearchPanel.set(false);
        this.showQuickSearchPanel.set(false);
        this.drawerSearchPanel.close();
        this.openSearchService._dataOpenLogic.set({
          showAllResults: false,
          activeDestroyComponent: false,
          showResponseView: true,
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroyAll();
  }

  destroyItemsLocalStorage() {
    const data: AnswerSearch[] = JSON.parse(localStorage.getItem('recentSearchItems') || '[]');
    if (data.length > 0) {
      data.forEach(x => (x.selected = false));
      localStorage.setItem('recentSearchItems', JSON.stringify(data));
    }
  }

  effectIsShowAllResults() {
    effect(() => {
      if (this.isShowAllResults()) {
        this.onShowExploreAll();
        this.isShowAllResults.set(false);
      }

      const s = this.drawerSearchPanel.state();
      if (s.isShowAllResults || s.showAllResults) {
        this.onShowExploreAll();
        this.drawerSearchPanel.patch({
          isShowAllResults: false,
          showAllResults: false,
        });
      }
    });
  }

  effectShowSearchPanel() {
    effect(() => {
      if (this.showSearchPanel() && this.searchType() === this.recent()) {
        this.openSearchService.getRecentSeaeches();
      }
    });
  }

  effectListenDestroy() {
    effect(() => {
      if (this.activeDestroyComponent()) {
        this.destroyAll(true);
      }
    });
  }

  effectIrtemSelected() {
    effect(() => {
      const data = this.items();
      data.forEach(x => {
        if (x.selected) this.onSelectItemSearch(x.text);
      });

      untracked(() => {
        const dr = this.drawerSearchPanel.state();
        if (dr.open && !dr.items.some(i => i.selected)) {
          this.drawerSearchPanel.setItems(data);
        }
      });
    });
  }
  effectSearchType() {
    effect(() => {
      if (this.searchType() === this.quick() || this.searchType() === this.recent()) {
        this.searchType.set(this.searchType() ?? SearchType.DEFAULT);
        this.setItems();
      }
    });
  }

  setItems() {
    if (this.searchType() === this.recent()) {
      return;
    } else {
      if (this.searchpanel?.showAllSignal()) {
        this.items.set(this.openSearchService.promts() || []);
      } else {
        this.items.set(this.openSearchService.promts()?.slice(0, 5) || []);
      }
    }
  }

  destroyAll(isNoCleanSearchType?: boolean) {
    this.destroyItemsLocalStorage();
    if (!isNoCleanSearchType) {
      this.searchType.set(SearchType.DEFAULT);
    }
    this.inputText.set(null);
    this.setIsShowAllResults(false);
    this.resetAfterDestroy();
    this.detectBackBySearchType();
    this.isShowAllResults.set(false);
    this.drawerSearchPanel.close();
  }

  resetAfterDestroy() {
    [0, 10, 50].forEach(delay => {
      setTimeout(() => {
        this.activeDestroyComponent.set(false);
      }, delay);
    });
  }

  openRecentSearch() {
    this.openSearchService.getRecentSeaeches();
    this.showRecentSearchPanel.set(false);
    this.showQuickSearchPanel.set(false);
    this.searchType.set(this.recent());
    this.showAllResults.set(false);
    this.searchType.set(this.recent());
    this.resetValueTextarea();
    this.resizeTextarea();

    this.drawerSearchPanel.open({
      searchType: this.recent(),
      items: this.items(),
      showAll: false,
      showAllResults: false,
      isShowAllResults: this.isShowAllResults(),
      showSearchPanel: this.showSearchPanel(),
    });
  }

  openQuickSearch() {
    this.showQuickSearchPanel.set(false);
    this.showRecentSearchPanel.set(false);
    this.showAllResults.set(false);
    this.openSearchService.getPromts(0, true);
    this.searchType.set(this.quick());
    this.resetValueTextarea();
    this.resizeTextarea();
    this.drawerSearchPanel.open({
      searchType: this.quick(),
      items: this.openSearchService.promts() || this.items(),
      showAll: false,
      showAllResults: false,
      isShowAllResults: this.isShowAllResults(),
      showSearchPanel: this.showSearchPanel(),
    });
  }

  resetValueTextarea() {
    this.inputText.set(null);
    const items = this.items().map(item => ({ ...item, selected: false }));
    this.items.set(items);
    this.destroyItemsLocalStorage();
  }

  onSelectItemSearch(text: TranslatableField): void {
    const convertPlainText = () => {
      return Object.entries(text).reduce<TranslatableField>((acc, [key, value]) => {
        if (value) {
          const parsed = new DOMParser().parseFromString(value, 'text/html');
          let plainText = parsed.body.textContent || '';
          plainText = plainText.replace(/\s+/g, ' ').trim();
          acc[key as keyof TranslatableField] = plainText;
        }
        return acc;
      }, {});
    };
    this.inputText.set(convertPlainText());
    this.resizeTextarea();
  }

  resizeTextarea() {
    setTimeout(() => {
      const textarea = document.querySelector<HTMLTextAreaElement>('.auto-grow-textarea');
      if (textarea) {
        this.adjustTextareaHeight(textarea);
      }
    });
  }

  adjustTextareaHeight(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  onExploreAll(type: SearchType) {
    this.setIsShowAllResults(true);
    this.searchType.set(type);
  }

  onBackToMainView() {
    if (this.spinnerConversational()) return;
    localStorage.removeItem('OpenSearchPath');
    const lastPath = localStorage.getItem('lastOpenSearchPath');
    const responseView = localStorage.getItem('responseView');
    const openedPanelFromResponse = this.openSearchService.openedPanelFromResponse();
    this.openSearchService.openedPanelFromResponse.set(false);
    this.openSearchService.historyResponse.set(null);
    this.openSearchService.conversationIdSignal.set(null);
    this.openSearchService._dataOpenLogic.set(null);
    this.resetValueTextarea();
    this.detectBackBySearchType(true);
    this.destroyItemsLocalStorage();
    this.drawerService.close();
    if (!openedPanelFromResponse && lastPath && responseView) {
      if (lastPath === 'quick') {
        this.searchType.set(SearchType.QUICK);
        this.onShowExploreAll();
      } else if (lastPath === 'recent') {
        this.searchType.set(SearchType.RECENT);
        this.onShowExploreAll();
      }
      this.setIsShowAllResults(true);
      this.showAllResults.set(true);
      this.drawerSearchPanel.close();
    } else {
      this.setIsShowAllResults(false);
      this.searchType.set(SearchType.QUICK);
      this.openSearchService.getPromts(0, true);
      this.drawerSearchPanel.open({
        searchType: SearchType.QUICK,
        items: this.openSearchService.promts() ?? [],
        showAll: false,
        showAllResults: false,
        isShowAllResults: false,
        showSearchPanel: this.showSearchPanel(),
      });
    }
    localStorage.removeItem('lastOpenSearchPath');
    localStorage.removeItem('responseView');
  }

  detectBackBySearchType(isClick = false) {
    if (this.showResponseView() && isClick) {
      this.showResponseView.set(false);
    }
    if (this.searchType() === this.quick()) {
      this.items.set(this.openSearchService.promts() || []);
      return;
    }
  }

  onShowExploreAll() {
    this.showAllResults.set(true);
    this.showQuickSearchPanel.set(false);
    this.showRecentSearchPanel.set(false);
    this.isShowAllResults.set(false);
    this.showResponseView.set(false);
    this.resetValueTextarea();
    this.drawerSearchPanel.close();
    if (this.searchType() === this.quick()) {
      this.openSearchService.getPromts(0, false);
      this.items.set(this.openSearchService.promts() || []);
    } else if (this.searchType() === this.recent()) {
      this.openSearchService.getRecentSeaeches();
    }
  }

  setIsShowAllResults = (value: boolean) => this.showAllResults.set(value);

  startOnboarding(reset = false): void {
    if (reset) {
      this.onBoardingService.resetTour(this.onBoardinbgKey);
    }
    void this.onBoardingService.startTour(StepHomeOpenSearch, this.onBoardinbgKey);
  }

  effectKeepViewStateInSync() {
    effect(() => {
      const showAllResults = this.showAllResults();
      const searchType = this.searchType();

      if (
        showAllResults &&
        (searchType === this.quick() || searchType === this.recent()) &&
        this.showResponseView()
      ) {
        untracked(() => {
          this.showResponseView.set(false);
          this.activeDestroyComponent.set(false);
          this.showRecentSearchPanel.set(false);
          this.showQuickSearchPanel.set(false);
          this.drawerSearchPanel.close();
          this.drawerService.close();
        });
      }
    });
  }

  effectResetResponseViewWhenConversationIsCleared() {
    effect(() => {
      const history = this.openSearchService.historyResponse();
      const viewState = this.openSearchService._dataOpenLogic();

      if (!history && !viewState && this.showResponseView()) {
        untracked(() => {
          this.showResponseView.set(false);
          this.activeDestroyComponent.set(false);
          this.showRecentSearchPanel.set(false);
          this.showQuickSearchPanel.set(false);
          this.drawerSearchPanel.close();
          this.drawerService.close();
        });
      }
    });
  }
  effectSyncSelectionFromDrawer() {
    effect(() => {
      const s = this.drawerSearchPanel.state();
      const selected = s.items.find(x => x?.selected);

      if (selected?.text) {
        this.onSelectItemSearch(selected.text);
        const cleared = s.items.map(x => ({ ...x, selected: false }));
        this.drawerSearchPanel.patch({ items: cleared });
      }
    });
  }

  ngOnInit(): void {
    const hideForever = localStorage.getItem('hideDisclaimerForever');
    if (hideForever === 'true') {
      this.showDisclaimer.set(false);
    }
  }

  onHideDisclaimerForNow(): void {
    this.showDisclaimer.set(false);
  }

  onHideDisclaimerForever(): void {
    localStorage.setItem('hideDisclaimerForever', 'true');
    this.showDisclaimer.set(false);
  }
}
